<template>
  <div class="layout-employee">
    <!-- ── 未选员工：员工列表 ── -->
    <div v-if="!currentEmployee" class="employee-gallery">
      <div class="gallery-head">
        <div class="t">高级 AI 数字员工</div>
        <div class="d">挑一位交代任务，他会把活干完再交给你</div>
      </div>
      <div v-loading="loadingEmployees" class="gallery-grid">
        <div v-for="e in employees" :key="e.guid" class="emp-card" @click="pickEmployee(e)">
          <el-avatar :size="56" :src="e.avatar" class="avatar">{{ e.name.slice(0, 1) }}</el-avatar>
          <div class="name">{{ e.name }}</div>
          <div class="title">{{ e.title }}</div>
          <div class="desc">{{ e.description }}</div>
          <div class="cost">单次消耗 {{ e.pointsCost }} 点</div>
        </div>
        <div v-if="!loadingEmployees && !employees.length" class="empty">
          还没有可用的 AI 员工
        </div>
      </div>
    </div>

    <!-- ── 已选员工：左工作列表 + 右工作区 ── -->
    <template v-else>
      <div class="task-panel">
        <div class="panel-head">
          <i class="el-icon-arrow-left back" title="返回员工列表" @click="backToGallery"></i>
          <span class="who">{{ currentEmployee.name }}</span>
        </div>
        <el-button type="primary" class="new-btn" icon="el-icon-plus" @click="startNewWork">
          开始新工作
        </el-button>
        <div class="task-list">
          <div
            v-for="t in tasks"
            :key="t.guid"
            class="task-item"
            :class="{ active: currentTask && currentTask.guid === t.guid }"
            @click="openTask(t.guid)"
          >
            <el-image v-if="t.coverUrl" :src="t.coverUrl" fit="cover" class="thumb" />
            <div v-else class="thumb placeholder"><i class="el-icon-picture-outline"></i></div>
            <div class="meta">
              <div class="tt" :title="t.title">{{ t.title }}</div>
              <div class="st">
                <span class="dot" :class="'s-' + t.status"></span>{{ t.statusText }}
              </div>
            </div>
          </div>
          <div v-if="!tasks.length" class="empty-tip">还没有工作记录</div>
        </div>
      </div>

      <div class="work-panel">
        <!-- 新工作：填表 -->
        <DynamicForm
          v-if="mode === 'form'"
          :schema="currentEmployee.formSchema"
          :points-cost="currentEmployee.pointsCost"
          :submitting="submitting"
          @submit="onSubmit"
        />
        <!-- 已有任务：事件流 -->
        <EventStream
          v-else-if="currentTask"
          :task="currentTask"
          :events="events"
          :points-cost="currentEmployee.pointsCost"
          @review="onReview"
          @revise="onRevise"
        />
      </div>

      <!-- ★右侧：小红书呈现效果预览。
           用户的终点是「发出去」，平铺看 6 张 PNG 判断不了发出去长什么样 —— 这块补的就是这个 -->
      <XhsPreview :task="currentTask" :mode="mode" :pending-images="pendingImages" />
    </template>
  </div>
</template>

<script>
import DynamicForm from './components/dynamic-form.vue'
import EventStream from './components/event-stream.vue'
import XhsPreview from './components/xhs-preview.vue'
import {
  employeeListApi,
  employeeTaskListApi,
  employeeStartApi,
  employeeTaskDetailApi,
  employeeProgressApi,
  employeeReviewApi,
  employeeReviseApi,
} from '@/apis/employee'

/** 轮询间隔。阶段是秒级的，1.5s 用户完全无感，比挂 SSE 长连接省心得多 */
const POLL_MS = 1500

export default {
  name: 'LayoutEmployee',
  components: { DynamicForm, EventStream, XhsPreview },

  data() {
    return {
      employees: [],
      loadingEmployees: false,
      currentEmployee: null,

      tasks: [],
      currentTask: null,
      events: [],

      mode: 'form', // form | task
      submitting: false,

      timer: null,
      lastId: 0,
    }
  },

  computed: {
    /**
     * 闸门③挂起时那一版的图。
     * ★取最后一张 review 闸门卡 —— 一次运行可能挂起多轮（改一次→再挂起一次），
     *   拿第一张的话用户改完看到的还是改之前的图。
     */
    pendingImages() {
      for (let i = this.events.length - 1; i >= 0; i--) {
        const ev = this.events[i]
        if (ev.type !== 'gate' || !ev.payload || ev.payload.gate !== 'review') continue
        const preview = (ev.payload.suspendPayload && ev.payload.suspendPayload.preview) || []
        return [...preview].sort((a, b) => a.index - b.index).map((p) => p.url).filter(Boolean)
      }
      return []
    },
  },

  mounted() {
    this.loadEmployees()
  },

  beforeDestroy() {
    // ★组件销毁必须停轮询，否则切走页面还在刷接口
    this.stopPoll()
  },

  methods: {
    async loadEmployees() {
      this.loadingEmployees = true
      try {
        const res = await employeeListApi()
        this.employees = (res && res.list) || []
      } finally {
        this.loadingEmployees = false
      }
    },

    pickEmployee(e) {
      this.currentEmployee = e
      this.mode = 'form'
      this.currentTask = null
      this.events = []
      this.loadTasks()
    },

    backToGallery() {
      this.stopPoll()
      this.currentEmployee = null
      this.currentTask = null
      this.events = []
    },

    async loadTasks() {
      if (!this.currentEmployee) return
      const res = await employeeTaskListApi({
        employeeGuid: this.currentEmployee.guid,
        page: 1,
        pageSize: 30,
      })
      this.tasks = (res && res.list) || []
    },

    startNewWork() {
      this.stopPoll()
      this.mode = 'form'
      this.currentTask = null
      this.events = []
    },

    async onSubmit(formData) {
      this.submitting = true
      try {
        const res = await employeeStartApi({
          employeeGuid: this.currentEmployee.guid,
          formData,
        })
        await this.loadTasks()
        await this.openTask(res.taskGuid)
      } finally {
        this.submitting = false
      }
    },

    /** 打开一个任务：先一次性拉全量重建，再按需轮询 */
    async openTask(taskGuid) {
      this.stopPoll()
      this.mode = 'task'
      const res = await employeeTaskDetailApi({ taskGuid })
      this.currentTask = res.task
      this.events = res.events || []
      this.lastId = this.events.length ? this.events[this.events.length - 1].id : 0

      if (!this.isFinished(res.task.status)) this.startPoll(taskGuid)
    },

    /**
     * 闸门确认 / 提修改意见。
     * ★确认之后必须立刻恢复轮询 —— 任务会从 awaiting_review 转回 running，
     *   不重启轮询的话用户点完按钮就再也看不到后续进度了。
     */
    async onReview({ gate, action, payload, resolve, reject }) {
      try {
        await employeeReviewApi({
          taskGuid: this.currentTask.guid,
          gate,
          action,
          payload: payload || {},
        })
        // 先把状态改掉，按钮立刻消失，别让用户重复点
        this.currentTask = { ...this.currentTask, status: 'running', statusText: '生成中' }
        this.stopPoll()
        this.startPoll(this.currentTask.guid)
        resolve()
      } catch (e) {
        reject(e)
      }
    },

    /**
     * 「再改一版」—— 定稿之后回来改。
     * ★和 review 的区别：review 是恢复挂着的 run，这个是**新起一个 run**
     *   （定稿后旧 run 已终态），带着上次的决策快照，所以不重新生图、不扣点。
     */
    async onRevise({ instruction, resolve, reject }) {
      try {
        await employeeReviseApi({ taskGuid: this.currentTask.guid, instruction })
        this.currentTask = { ...this.currentTask, status: 'running', statusText: '生成中' }
        this.stopPoll()
        this.startPoll(this.currentTask.guid)
        resolve()
      } catch (e) {
        reject(e)
      }
    },

    startPoll(taskGuid) {
      this.timer = setInterval(async () => {
        // ★页面隐藏时不轮询：用户切走了还刷接口纯属浪费
        if (document.hidden) return
        try {
          const res = await employeeProgressApi({ taskGuid, afterId: this.lastId })
          if (res.events && res.events.length) {
            this.events = this.events.concat(res.events)
            this.lastId = res.lastId
          }
          this.currentTask = res.task
          if (res.finished) {
            this.stopPoll()
            this.loadTasks() // 刷新左侧列表的状态与封面
          }
        } catch (e) {
          // 单次轮询失败不停表 —— 网络抖一下不该让进度永远卡住
          console.warn('[employee] 轮询失败', e)
        }
      }, POLL_MS)
    },

    stopPoll() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    isFinished(status) {
      return ['done', 'failed', 'cancelled'].indexOf(status) >= 0
    },
  },
}
</script>

<style lang="scss">
.layout-employee {
  display: flex;
  /* ★父级是 flex row，默认 flex:0 1 auto —— 不写这两行本容器就按内容宽度收缩（实测卡在
     1474px），窗口再宽也不长，右侧预览栏贴不到边缘，后面留一大片死区。
     min-width:0 是配套的：没有它，里面 flex 子项的内容能把整行顶出去。 */
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  background: #fff;

  /* ── 员工列表 ── */
  .employee-gallery {
    flex: 1;
    padding: 32px 40px;
    overflow-y: auto;

    .gallery-head {
      margin-bottom: 24px;
      .t { font-size: 22px; font-weight: 600; color: #1f2329; }
      .d { margin-top: 6px; font-size: 13px; color: #8a8f98; }
    }
    .gallery-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 18px;
    }
    .emp-card {
      width: 236px;
      padding: 22px 20px;
      border: 1px solid #ebedf0;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.18s;
      &:hover { border-color: var(--primary, #4b7bff); box-shadow: 0 6px 20px rgba(0, 0, 0, 0.07); transform: translateY(-2px); }
      .avatar { background: #f0f3f8; color: #4b7bff; font-size: 20px; }
      .name { margin-top: 12px; font-size: 16px; font-weight: 600; color: #1f2329; }
      .title { margin-top: 2px; font-size: 12px; color: #4b7bff; }
      .desc { margin-top: 10px; font-size: 12px; line-height: 1.6; color: #8a8f98; min-height: 38px; }
      .cost { margin-top: 12px; font-size: 12px; color: #f08c3a; }
    }
    .empty { color: #b0b4bb; font-size: 13px; padding: 40px 0; }
  }

  /* ── 左侧工作列表 ── */
  .task-panel {
    width: 280px;
    border-right: 1px solid #ebedf0;
    display: flex;
    flex-direction: column;
    background: #fafbfc;

    .panel-head {
      height: 52px;
      display: flex;
      align-items: center;
      padding: 0 14px;
      border-bottom: 1px solid #ebedf0;
      .back { cursor: pointer; font-size: 16px; color: #8a8f98; margin-right: 8px; &:hover { color: #4b7bff; } }
      .who { font-size: 15px; font-weight: 600; color: #1f2329; }
    }
    .new-btn { margin: 14px; width: calc(100% - 28px); }
    .task-list { flex: 1; overflow-y: auto; padding: 0 10px 14px; }
    .task-item {
      display: flex;
      padding: 9px;
      border-radius: 8px;
      cursor: pointer;
      &:hover { background: #f0f3f8; }
      &.active { background: #e8efff; }
      .thumb { width: 42px; height: 56px; border-radius: 6px; flex-shrink: 0; background: #eef0f4; }
      .thumb.placeholder { display: flex; align-items: center; justify-content: center; color: #c3c7ce; font-size: 18px; }
      .meta { margin-left: 10px; overflow: hidden; display: flex; flex-direction: column; justify-content: center; }
      .tt { font-size: 13px; color: #1f2329; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .st { margin-top: 5px; font-size: 12px; color: #8a8f98; display: flex; align-items: center; }
      .dot { width: 6px; height: 6px; border-radius: 50%; margin-right: 5px; background: #c3c7ce; }
      .dot.s-running { background: #4b7bff; }
      .dot.s-awaiting_review { background: #f0a020; }
      .dot.s-done { background: #34c759; }
      .dot.s-failed { background: #f56c6c; }
    }
    .empty-tip { text-align: center; color: #b0b4bb; font-size: 12px; padding: 30px 0; }
  }

  /* ── 右侧工作区 ── */
  .work-panel { flex: 1; overflow-y: auto; }
}
</style>
